import type { FunctionComponent } from 'preact';
import { useState } from 'preact/hooks';
import { t } from '../../../domain/localisation';
import { SmileIdLogo, CheckIcon, ChevronDownIcon } from './assets/icons';

// Icon URLs — Vite resolves these to URL strings via the `*.svg` ambient
// module declaration in lib/types.d.ts.

import phoneIcon from './assets/phone.svg';

import personIcon from './assets/person.svg';

import idCardIcon from './assets/id-card.svg';

import deviceIcon from './assets/device.svg';

interface ConsentViewProps {
  themeColor: string;
  hideAttribution: boolean;
  partnerName?: string;
  partnerLogo?: string;
  policyUrl?: string;
  /**
   * Initial state of the consent checkbox. Regulated (EU-class) regions
   * must start un-ticked — the region decision is made by the host element
   * (see `Consent` / `lib/domain/region`), never here. The Allow button
   * stays disabled while un-ticked either way, which is what blocks
   * progression until explicit consent.
   */
  initialAccepted?: boolean;
  onGranted: () => void;
  onDenied: () => void;
}

const ITEMS: { icon: string; key: string }[] = [
  { icon: phoneIcon, key: 'consentScreen.items.contact' },
  { icon: personIcon, key: 'consentScreen.items.personal' },
  { icon: idCardIcon, key: 'consentScreen.items.biometric' },
  { icon: deviceIcon, key: 'consentScreen.items.device' },
];

/**
 * Returns the input only if it is a syntactically valid absolute URL whose
 * protocol is `http:` or `https:`. Anything else (including `javascript:`,
 * `data:`, malformed strings, or non-strings) is rejected. This prevents a
 * malicious partner config from injecting an XSS vector through props that
 * are rendered as `href`/`src`.
 */
const safeHttpUrl = (value?: string): string | undefined => {
  if (typeof value !== 'string' || value.length === 0) return undefined;
  try {
    const parsed = new URL(value);
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      return parsed.toString();
    }
  } catch {
    /* fall through */
  }
  return undefined;
};

/**
 * Region-aware consent screen. Product-agnostic: mounted by the embed product
 * scripts (v3 SmartSelfie, DocV, Biometric KYC, …) as the first step of a v3
 * flow. Intentionally separate from the legacy `<end-user-consent>` element
 * used by pre-v3 KYC products — modifying that element would change behaviour
 * for every other product.
 */
export const ConsentView: FunctionComponent<ConsentViewProps> = ({
  themeColor,
  hideAttribution,
  partnerName,
  partnerLogo,
  policyUrl,
  initialAccepted = false,
  onGranted,
  onDenied,
}) => {
  const [accepted, setAccepted] = useState(initialAccepted);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const partner = partnerName;
  const safePolicyUrl = safeHttpUrl(policyUrl);
  const safePartnerLogo = safeHttpUrl(partnerLogo);

  return (
    <div className="consent-screen">
      <div className="logos">
        {safePartnerLogo && (
          <div className="logo partner-logo">
            <img src={safePartnerLogo} alt={partner} />
          </div>
        )}
        <div className="logo smile-logo" aria-hidden="true">
          <SmileIdLogo />
        </div>
      </div>

      <h1 className="title">
        {partner} {t('consentScreen.titleSuffix')}{' '}
        <span style={{ whiteSpace: 'nowrap' }}>Smile ID.</span>
      </h1>

      <p className="body">
        {t('consentScreen.bodyPrefix')}
        <strong>Smile ID</strong>
        {t('consentScreen.bodyMiddle')}
        <strong>{partner}</strong>
        {t('consentScreen.bodySuffix')}
      </p>

      <ul className="items">
        {ITEMS.map((it) => (
          <li key={it.key}>
            <img src={it.icon} alt="" aria-hidden="true" />
            <span>{t(it.key)}</span>
          </li>
        ))}
      </ul>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted((e.target as HTMLInputElement).checked)}
        />
        <span className="box" aria-hidden="true">
          <CheckIcon />
        </span>
        <span className="copy">{t('consentScreen.consentCheckbox')}</span>
      </label>

      <button
        type="button"
        className="learn-more"
        onClick={() => setLearnMoreOpen((v) => !v)}
        aria-expanded={learnMoreOpen}
      >
        <span className="chevron" data-open={learnMoreOpen} aria-hidden="true">
          <ChevronDownIcon />
        </span>
        <span>{t('consentScreen.learnMore')}</span>
      </button>

      {learnMoreOpen && (
        <p className="learn-more-copy">
          {t('consentScreen.learnMoreBodyPrefix')}
          <strong>Smile ID</strong>
          {t('consentScreen.learnMoreBodyAnd')}
          <strong>{partner}</strong>
          {t('consentScreen.learnMoreBodySuffix')}
          <br />
          {t('consentScreen.learnMoreShare')}
          <br />
          {t('consentScreen.learnMoreRightsPrefix')}{' '}
          <a
            href="https://dsar.usesmileid.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: themeColor }}
          >
            {t('consentScreen.dsarLink')}
          </a>{' '}
          {t('consentScreen.learnMoreRightsMiddle')}{' '}
          <a
            href="https://usesmileid.com/legal/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: themeColor }}
          >
            Smile ID
          </a>
          {safePolicyUrl ? (
            <>
              {' '}
              {t('consentScreen.partnerPrivacyTailLinked')}{' '}
              <a
                href={safePolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: themeColor }}
              >
                {partner}
              </a>{' '}
              {t('consentScreen.partnerPrivacyTailEnd')}
            </>
          ) : (
            <>
              {' '}
              {t('consentScreen.partnerPrivacyTailLinked')}{' '}
              <strong>{partner}</strong>{' '}
              {t('consentScreen.partnerPrivacyTailEnd')}
            </>
          )}
        </p>
      )}

      <div className="actions">
        <button
          type="button"
          className="allow"
          style={{ background: themeColor, borderColor: themeColor }}
          disabled={!accepted}
          onClick={onGranted}
        >
          {t('consentScreen.allow')}
        </button>
        <button
          type="button"
          className="deny"
          style={{ color: themeColor, borderColor: themeColor }}
          onClick={onDenied}
        >
          {t('consentScreen.deny')}
        </button>
      </div>

      {!hideAttribution && (
        // @ts-expect-error preact-custom-element types
        <powered-by-smile-id />
      )}

      <style>{`
        :host {
          display: block;
          height: 100%;
          /* Makes this element the reference box for the cqi units below, so
             type is sized against the card the screen actually occupies
             rather than the browser window. */
          container-type: inline-size;
        }
        .consent-screen {
          /* Type scale.
             ---------------------------------------------------------------
             Text and icons key off the CONTAINER's inline size (cqi), never
             the viewport. The host card is width-capped by the embed shell
             (main { max-width: 40ch }), so it does not widen on a large
             screen; sizing type off dvh made it drift out of proportion with
             the column — pinned at the clamp ceiling while the card grew
             taller around it, which reads as "the text got smaller".
             Vertical SPACING below still keys off dvh: that is what decides
             whether the screen fits without scrolling on a short viewport. */
          --fs: clamp(13px, 4.2cqi, 16px);
          --fs-body: calc(var(--fs) * 0.9);
          --fs-title: calc(var(--fs) * 1.35);
          --fs-action: calc(var(--fs) * 1.1);
          /* Icon lane: every row leads with an --icon-wide column, so the
             smaller controls (checkbox, chevron) are centred in that lane by
             --lane-inset and the trailing text always starts at the same x. */
          --icon: calc(var(--fs) * 1.75);
          --box: calc(var(--fs) * 1.25);
          --lane-inset: calc((var(--icon) - var(--box)) / 2);
          --text-gap: 0.85rem;

          display: flex;
          flex-direction: column;
          min-height: 100%;
          padding: clamp(0.75rem, 2.5dvh, 1.5rem) clamp(1rem, 4vw, 1.25rem) clamp(0.5rem, 1.5dvh, 0.5rem);
          font-family: "DM Sans", system-ui, sans-serif;
          font-size: var(--fs);
          background: #F5F7FA;
          color: #1A1A1A;
          box-sizing: border-box;
        }
        .consent-screen > powered-by-smile-id {
          display: block;
          width: 100%;
          padding-top: clamp(0.5rem, 1.5dvh, 1rem);
          background: #F5F7FA;
          flex-shrink: 0;
        }
        .logos {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: clamp(0.5rem, 1.5dvh, 1.25rem);
          margin-bottom: clamp(0.75rem, 2.25dvh, 1.75rem);
          flex-shrink: 0;
        }
        /* The two circles overlap by design, but only their padding rings may
           intersect — the artwork inside is inset to 24px of the 40px circle
           so the overlap never covers a logo. */
        .logo {
          width: 40px;
          height: 40px;
          box-sizing: border-box;
          border-radius: 50%;
          background: #F9F0E7CC;
          border: 2px solid #F9F0E7CC;
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          position: relative;
          flex-shrink: 0;
        }
        .partner-logo { z-index: 2; }
        .partner-logo img {
          display: block;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          /* contain, not cover: a non-square partner logo is shown whole
             rather than cropped. */
          object-fit: contain;
        }
        .smile-logo {
          margin-left: -8px;
          z-index: 1;
        }
        .smile-logo svg { display: block; width: 24px; height: 24px; }
        .title {
          font-family: "DM Sans", system-ui, sans-serif;
          font-size: var(--fs-title);
          font-weight: 600;
          line-height: 1.25;
          text-align: center;
          color: #21232C;
          margin: 0 0 clamp(0.5rem, 1.2dvh, 1rem);
          flex-shrink: 0;
        }
        .body {
          color: #5E646E;
          text-align: justify;
          font-family: "DM Sans", system-ui, sans-serif;
          font-size: var(--fs-body);
          font-style: normal;
          font-weight: 400;
          line-height: 1.35;
          margin: 0 0 clamp(0.5rem, 1.2dvh, 1rem);
          flex-shrink: 0;
        }
        .body strong {
          color: #5E646E;
          font-family: "DM Sans", system-ui, sans-serif;
          font-size: inherit;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
        }
        .items {
          list-style: none;
          padding: 0;
          margin: 0 0 clamp(0.5rem, 1.2dvh, 1rem);
          display: flex;
          flex-direction: column;
          gap: clamp(6px, 1.4dvh, 16px);
          flex-shrink: 0;
        }
        .items li {
          display: flex;
          align-items: center;
          gap: var(--text-gap);
          color: #21232C;
          font-family: "DM Sans", system-ui, sans-serif;
          font-size: var(--fs-body);
          font-style: normal;
          font-weight: 400;
          line-height: 1.35;
        }
        .items img {
          width: var(--icon);
          height: var(--icon);
          flex-shrink: 0;
          object-fit: contain;
          object-position: left center;
        }
        .checkbox {
          display: flex;
          align-items: flex-start;
          /* gap = items gap + the lane inset, so the trailing text column
             lines up with the item text above. */
          gap: calc(var(--text-gap) + var(--lane-inset));
          margin-bottom: clamp(0.4rem, 1dvh, 0.75rem);
          cursor: pointer;
          flex-shrink: 0;
        }
        .checkbox input { position: absolute; opacity: 0; pointer-events: none; }
        .checkbox .box {
          width: var(--box);
          height: var(--box);
          /* Centres the box within the icon lane used by .items img so the
             icon column visually aligns with those above. */
          margin-left: var(--lane-inset);
          border-radius: 4px;
          background: transparent;
          border: 1.5px solid #2D2B2A;
          color: #F9F0E7;
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          transition: background 120ms ease, border-color 120ms ease;
        }
        .checkbox .box svg {
          display: block;
          width: 70%;
          height: 70%;
          opacity: 0;
          transition: opacity 120ms ease;
        }
        .checkbox input:checked + .box {
          background: #2D2B2A;
          border-color: #2D2B2A;
        }
        .checkbox input:checked + .box svg { opacity: 1; }
        .checkbox .copy {
          color: #5E646E;
          text-align: justify;
          font-family: "DM Sans", system-ui, sans-serif;
          font-size: var(--fs-body);
          font-style: normal;
          font-weight: 400;
          line-height: 1.35;
        }
        .learn-more {
          display: inline-flex;
          align-items: center;
          /* Match the checkbox/items icon column so trailing text aligns. */
          gap: calc(var(--text-gap) + var(--lane-inset));
          background: transparent;
          border: none;
          padding: 0;
          color: #21232C;
          font-family: "DM Sans", system-ui, sans-serif;
          font-size: var(--fs-body);
          font-style: normal;
          font-weight: 700;
          line-height: normal;
          cursor: pointer;
          margin-bottom: clamp(0.3rem, 0.8dvh, 0.5rem);
          flex-shrink: 0;
        }
        .learn-more .chevron {
          width: var(--box);
          height: var(--box);
          /* Centres the chevron within the icon lane to align with the icons
             above. */
          margin-left: var(--lane-inset);
          border-radius: 50%;
          background: #2D2B2A;
          color: #F9F0E7;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: transform 150ms ease;
          flex-shrink: 0;
        }
        .learn-more .chevron svg { width: 80%; height: 80%; }
        .learn-more .chevron[data-open="true"] { transform: rotate(180deg); }
        .learn-more-copy {
          color: #5E646E;
          text-align: justify;
          font-family: "DM Sans", system-ui, sans-serif;
          font-size: var(--fs-body);
          font-style: normal;
          font-weight: 400;
          line-height: 1.35;
          margin: 0 0 0.5rem;
          flex-shrink: 1;
          overflow: hidden;
        }
        .learn-more-copy strong {
          color: #5E646E;
          font-family: "DM Sans", system-ui, sans-serif;
          font-size: inherit;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
        }
        .actions {
          display: flex;
          flex-direction: column;
          gap: clamp(0.4rem, 1dvh, 0.75rem);
          align-items: stretch;
          padding-top: clamp(0.5rem, 1.5dvh, 1.5rem);
          flex-shrink: 0;
        }
        .allow {
          width: 100%;
          height: clamp(40px, 6dvh, 48px);
          padding: 0 1rem;
          border-radius: 24px;
          border: 1px solid;
          color: white;
          font-size: var(--fs-action);
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
        }
        .allow:disabled { opacity: 0.45; cursor: not-allowed; }
        .deny {
          width: 100%;
          height: clamp(40px, 6dvh, 48px);
          padding: 0 1rem;
          background: transparent;
          border: 1px solid;
          border-radius: 24px;
          font-size: var(--fs-action);
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
        }
      `}</style>
    </div>
  );
};

export default ConsentView;
